import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog.tsx";
import { FormCreator, FormFieldDef } from "@/components/shared/form-creator";

const schema = z.object({
  file: z.union([z.instanceof(File), z.string()]),
  expiresAt: z.date().optional().or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onUpload: (file: File, expiresAt?: string | Date) => Promise<void>;
  uploading?: boolean;
}

export default function DocumentUploadModal({ open, onOpenChange, onUpload }: Props) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { file: undefined, expiresAt: "" },
  });

  const fields: FormFieldDef[] = [
    {
      type: "file",
      name: "file",
      label: "Document",
      accept: "application/pdf,image/*,.pdf",
      placeholder: "Drag & drop a PDF or image, or click to select",
      required: true,
    },
    {
      type: "date",
      name: "expiresAt",
      label: "Expires At",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
        </DialogHeader>

        <FormCreator
          form={form}
          onSubmit={(values: FormValues) => onUpload(values.file as File, values.expiresAt || undefined)}
          formDefinition={fields}
          buttonText="Upload"
        />
      </DialogContent>
    </Dialog>
  );
}
